import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { db } from './firebase';
import { collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, where, limit, startAfter, updateDoc } from 'firebase/firestore';
import { defaultContent } from './defaultContent';

// Importera svenska för att få måndag som första dag (Söndag längst till höger)
import sv from 'date-fns/locale/sv';
registerLocale('sv', sv);

const ALLERGEN_LIST = [
    { code: 'GH',  emoji: '🌾', label: 'Gluten' },
    { code: 'JP',  emoji: '🥜', label: 'Peanøtter' },
    { code: 'SES', emoji: '🌻', label: 'Sesamfrø' },
    { code: 'SO',  emoji: '🫘', label: 'Soya' },
    { code: 'F',   emoji: '🐟', label: 'Fisk' },
    { code: 'NØ',  emoji: '🌰', label: 'Nøtter' },
    { code: 'E',   emoji: '🥚', label: 'Egg' },
    { code: 'SK',  emoji: '🦐', label: 'Skalldyr' },
    { code: 'SEN', emoji: '🌿', label: 'Sennep' },
    { code: 'BL',  emoji: '🦑', label: 'Bløtdyr' },
    { code: 'LUP', emoji: '🌸', label: 'Lupin' },
    { code: 'M',   emoji: '🥛', label: 'Melk' },
    { code: 'SEL', emoji: '🥬', label: 'Selleri' },
    { code: 'SV',  emoji: '🍷', label: 'Svoveldioksyd/Sulfitter' },
];

const Admin = ({ content, onSave }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [activeTab, setActiveTab] = useState('bookings');

    // SEO: håll admin-sidan utanför sökmotorernas index
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        const prevTitle = document.title;
        document.title = 'Admin – Tufte Gård';
        return () => {
            document.head.removeChild(meta);
            document.title = prevTitle;
        };
    }, []);

    // CONTENT DATA
    const [formData, setFormData] = useState(content);

    // BOOKING DATA
    const [bookings, setBookings] = useState([]); // Endast kommande
    const [historyBookings, setHistoryBookings] = useState([]); // Historiska
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editingBooking, setEditingBooking] = useState(null); // För redigering

    // Historik state
    const [showHistory, setShowHistory] = useState(false);
    const [lastHistoryDoc, setLastHistoryDoc] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [allBookingsForCalendar, setAllBookingsForCalendar] = useState([]); // För kalenderns röd/grön prickar

    // Login & Status
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [message, setMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Manuell bokning - State
    const [manualBooking, setManualBooking] = useState({
        date: '',
        name: 'Manuell Bokning',
        guests: 4,
        phone: '',
        time: '',
        dishes: {}
    });

    const PASSWORD = "tufte";
    const MAX_BOOKINGS_PER_DAY = 5;

    // --- INIT ---
    useEffect(() => {
        const sessionAuth = sessionStorage.getItem('isAdminAuthenticated');
        if (sessionAuth === 'true') {
            setIsAuthenticated(true);
            fetchBookings();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        setFormData(prev => ({
            ...content,
            hero: {
                ...defaultContent.hero,
                ...content.hero
            },
            menu: {
                ...content.menu,
                mainCourses: content.menu.mainCourses || [],
                drinksGroups: content.menu.drinksGroups || []
            },
            events: content.events || [], // Init events array
            calendar: {
                openDates: content.calendar?.openDates || []
            },
            booking: {
                ...content.booking,
                standardTimes: content.booking?.standardTimes || ["18:00"],
                sundayTimes: content.booking?.sundayTimes || ["15:00"]
            },
            gallery: {
                ...content.gallery,
                images: content.gallery?.images || defaultContent.gallery.images || []
            },
            confirmationPopup: {
                ...defaultContent.confirmationPopup,
                ...content.confirmationPopup
            }
        }));
    }, [content]);

    // HÄMTA
    const fetchBookings = async () => {
        if (!db) return;
        setLoadingBookings(true);
        try {
            // Hämta ALLA för kalendern (vi optimerar detta senare om det blir segt, men bra för counts)
            const qAll = query(collection(db, "bookings"));
            const allSnap = await getDocs(qAll);
            const allList = [];
            allSnap.forEach(doc => allList.push({ id: doc.id, ...doc.data() }));
            setAllBookingsForCalendar(allList);

            // Filtrera ut kommande client-side (för att slippa index-strul just nu)
            const today = new Date().toISOString().split('T')[0];
            const upcoming = allList.filter(b => b.date >= today);
            setBookings(upcoming);

            // Om vi redan har laddat historik, rensa den inte, men uppdatera den kanske? 
            // För enkelhetens skull, nollställ historik vid reload så man får hämta på nytt
            // setHistoryBookings([]); 
            // setLastHistoryDoc(null);

        } catch (error) {
            console.error(error);
            alert("Kunde inte hämta bokningar: " + error.message);
        }
        setLoadingBookings(false);
    };

    const fetchHistory = async () => {
        if (!db) return;
        setHistoryLoading(true);
        try {
            const today = new Date().toISOString().split('T')[0];

            // Query: Datum < idag, sortera desc, limit 20
            // OBS: Detta KRÄVER ett index: date DESC
            // Om det kraschar, måste användaren skapa index via länken i loggen.
            let q = query(
                collection(db, "bookings"),
                where("date", "<", today),
                orderBy("date", "desc"),
                limit(20)
            );

            if (lastHistoryDoc) {
                // q = query(q, startAfter(lastHistoryDoc)); 
                // Fix: startAfter behöver att man bygger om queryn
                q = query(
                    collection(db, "bookings"),
                    where("date", "<", today),
                    orderBy("date", "desc"),
                    startAfter(lastHistoryDoc),
                    limit(20)
                );
            }

            const snap = await getDocs(q);
            const list = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));

            setHistoryBookings(prev => [...prev, ...list]);
            if (snap.docs.length > 0) {
                setLastHistoryDoc(snap.docs[snap.docs.length - 1]);
            } else {
                alert("Inga fler historiska bokningar.");
            }

        } catch (error) {
            console.error("Fel vid historik-hämtning:", error);
            alert("Kunde inte hämta historik (kräver index?): " + error.message);
        }
        setHistoryLoading(false);
    };

    const getIncidenceByDate = () => {
        const counts = {};
        // Använd ALLA bokningar för kalendern, inte bara de som visas i listan
        allBookingsForCalendar.forEach(b => {
            counts[b.date] = (counts[b.date] || 0) + 1;
        });
        return counts;
    };
    const bookingCounts = getIncidenceByDate();

    // AUTH
    const handleLogin = (e) => {
        e.preventDefault();
        // Enkelt lösenord
        if (password === PASSWORD) {
            setIsAuthenticated(true);
            sessionStorage.setItem('isAdminAuthenticated', 'true');
            setLoginError('');
        } else {
            setLoginError('Fel lösenord.');
        }
    };
    const handleLogout = () => {
        setIsAuthenticated(false);
        sessionStorage.removeItem('isAdminAuthenticated');
    };

    // CONTENT SAVE
    const handleSaveContent = async () => {
        setIsSaving(true);
        setMessage('');
        try {
            const success = await onSave(formData);
            if (success !== false) {
                setMessage('✅ Uppdaterat! Alla ändringar är sparade.');
                setTimeout(() => setMessage(''), 3000);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                alert("Fel vid sparning.");
            }
        } catch (error) {
            alert("Ett fel inträffade.");
        } finally {
            setIsSaving(false);
        }
    };

    // --- MANUELL BOKNING LOGIK --- 

    // Hjälpfunktion för att hämta giltiga tider för ett datum
    const getAvailableTimesForDate = (dateStr) => {
        if (!dateStr) return [];
        const dateObj = new Date(dateStr);
        const day = dateObj.getDay();
        // 0 = Sön
        if (day === 0) return formData.booking?.sundayTimes || [];
        return formData.booking?.standardTimes || [];
    };

    // När man klickar i Kalendern
    const handleOverviewDateSelect = (date) => {
        if (!date) {
            setSelectedDate(null);
            return;
        }
        // Justera tidszon
        const dStr = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        setSelectedDate(dStr);

        // Sätt också datumet i manuella formuläret
        setManualBooking(prev => ({
            ...prev,
            date: dStr,
            time: '' // Nollställ tid eftersom tiderna kan vara olika
        }));
    };

    const handleManualDishChange = (dishTitle, count) => {
        setManualBooking(prev => ({
            ...prev,
            dishes: {
                ...prev.dishes,
                [dishTitle]: parseInt(count)
            }
        }));
    };

    const addManualBooking = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, "bookings"), {
                ...manualBooking,
                dishes: manualBooking.dishes || {},
                createdAt: new Date().toISOString()
            });
            alert("Bokning inlagd!");
            fetchBookings(); // VIKTIGT: Uppdaterar listan

            // Återställ inte datum, så man kan lägga in fler på samma dag enkelt
            setManualBooking(prev => ({
                ...prev,
                name: 'Manuell Bokning',
                phone: '',
                guests: 4,
                dishes: {}
            }));
        } catch (error) {
            alert("Fel: " + error.message);
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm("Ta bort bokning?")) return;
        try {
            await deleteDoc(doc(db, "bookings", id));
            // Update local state directly to make UI feel instant and handle historical records.
            setBookings(prev => prev.filter(b => b.id !== id));
            setHistoryBookings(prev => prev.filter(b => b.id !== id));
            setAllBookingsForCalendar(prev => prev.filter(b => b.id !== id));
            fetchBookings(); // Keeps background state fresh
        } catch (e) {
            alert("Kunde inte ta bort: " + e.message);
        }
    }

    const handleUpdateBooking = async (e) => {
        e.preventDefault();
        if (!editingBooking) return;
        try {
            const updatedData = {
                ...editingBooking,
                dishes: editingBooking.dishes || {}
            };
            await updateDoc(doc(db, "bookings", editingBooking.id), updatedData);
            alert("Bokning uppdaterad!");

            // Update local state for both history and upcoming lists
            setBookings(prev => prev.map(b => b.id === editingBooking.id ? updatedData : b));
            setHistoryBookings(prev => prev.map(b => b.id === editingBooking.id ? updatedData : b));
            setAllBookingsForCalendar(prev => prev.map(b => b.id === editingBooking.id ? updatedData : b));

            setEditingBooking(null);
            fetchBookings(); // Keeps background state fresh 
        } catch (error) {
            alert("Kunde inte uppdatera: " + error.message);
        }
    };


    // --- HELPERS (Content editing) ---
    const handleChange = (section, key, value) => {
        setFormData(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
    };
    const handleObjectArrayChange = (section, listKey, index, fieldKey, value) => {
        setFormData(prev => {
            // Handle top-level array
            if (listKey === null) {
                const newArray = [...(prev[section] || [])];
                if (newArray[index]) {
                    newArray[index] = { ...newArray[index], [fieldKey]: value };
                }
                return { ...prev, [section]: newArray };
            }
            // Handle nested array
            const currentList = prev[section]?.[listKey] || [];
            const newList = [...currentList];
            if (newList[index]) {
                newList[index] = { ...newList[index], [fieldKey]: value };
            }
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [listKey]: newList
                }
            };
        });
    };
    const handleAddItem = (section, listKey, newItem) => {
        setFormData(prev => {
            if (listKey === null) {
                return { ...prev, [section]: [...(prev[section] || []), newItem] };
            }
            const currentList = prev[section]?.[listKey] || [];
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [listKey]: [...currentList, newItem]
                }
            };
        });
    };
    const handleDeleteItem = (section, listKey, index) => {
        if (!window.confirm("Ta bort?")) return;
        setFormData(prev => {
            if (listKey === null) {
                const newArray = [...(prev[section] || [])];
                newArray.splice(index, 1);
                return { ...prev, [section]: newArray };
            }
            const currentList = prev[section]?.[listKey] || [];
            const newList = currentList.filter((_, i) => i !== index);
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [listKey]: newList
                }
            };
        });
    };

    const handleMoveItem = (section, listKey, index, direction) => {
        setFormData(prev => {
            const list = listKey ? (prev[section]?.[listKey] || []) : (prev[section] || []);
            if (direction === -1 && index === 0) return prev; // Can't move up
            if (direction === 1 && index === list.length - 1) return prev; // Can't move down

            const newList = [...list];
            const temp = newList[index];
            newList[index] = newList[index + direction];
            newList[index + direction] = temp;

            if (listKey) {
                return {
                    ...prev,
                    [section]: {
                        ...prev[section],
                        [listKey]: newList
                    }
                };
            } else {
                return {
                    ...prev,
                    [section]: newList
                };
            }
        });
    };

    const handleImageUpload = (e, section, listKey) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) { // 2MB limit check
            alert("Filen är för stor (Max 2MB). Försök komprimera den först.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            // Create new image object
            const newImage = { url: reader.result, category: 'vinter' }; // Default to vinter
            handleAddItem(section, listKey, newImage);
        };
        reader.readAsDataURL(file);
    };
    const handleArrayChange = (section, key, index, value) => {
        const newArray = [...formData[section][key]];
        newArray[index] = value;
        setFormData(prev => ({ ...prev, [section]: { ...prev[section], [key]: newArray } }));
    };
    const handleCalendarDateChange = (date) => {
        if (!date) return;
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - (offset * 60 * 1000));
        const dateString = localDate.toISOString().split('T')[0];

        const currentDates = formData.calendar?.openDates || [];
        let newDates;
        if (currentDates.includes(dateString)) {
            newDates = currentDates.filter(d => d !== dateString);
        } else {
            newDates = [...currentDates, dateString].sort();
        }
        setFormData(prev => ({ ...prev, calendar: { ...prev.calendar, openDates: newDates } }));
    };
    const generateWeekends = () => {
        if (!window.confirm("Detta lägger till helger 3 månader framåt. Spara efteråt!")) return;
        const dates = new Set(formData.calendar?.openDates || []);
        const today = new Date();
        const end = new Date(); end.setMonth(today.getMonth() + 3);
        let curr = new Date(today);
        while (curr <= end) {
            const d = curr.getDay();
            if (d === 0 || d === 5 || d === 6) dates.add(curr.toISOString().split('T')[0]);
            curr.setDate(curr.getDate() + 1);
        }
        setFormData(prev => ({ ...prev, calendar: { ...prev.calendar, openDates: Array.from(dates).sort() } }));
    };
    const parseDates = (ds) => (ds || []).map(d => new Date(d));


    if (!isAuthenticated) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#1a1a1a', color: '#fff' }}>
                <form onSubmit={handleLogin} style={{ padding: '2rem', background: '#333', borderRadius: '8px' }}>
                    <h2 style={{ marginBottom: '1rem' }}>Admin Login</h2>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Lösenord" style={{ padding: '10px', width: '100%', marginBottom: '10px' }} />
                    <button type="submit" style={{ width: '100%', padding: '10px', background: '#c5a059', border: 'none', cursor: 'pointer' }}>Logga in</button>
                    {loginError && <p style={{ color: 'red', marginTop: '10px' }}>{loginError}</p>}
                </form>
            </div>
        );
    }

    const activeList = showHistory ? historyBookings : bookings;

    const sortedBookings = [...activeList].sort((a, b) => {
        if (a.date !== b.date) {
            // Om history: sortera fallande (nyast först). Om kommande: sortera stigande (närmast först).
            return showHistory ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
        }
        return a.time.localeCompare(b.time);
    });
    const displayedBookings = selectedDate
        ? sortedBookings.filter(b => b.date === selectedDate)
        : sortedBookings;

    // Hämta tider för valda datumet i manuella rutan
    const manualTimes = getAvailableTimesForDate(manualBooking.date);

    return (
        <div className="admin-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', color: '#fff', backgroundColor: '#1a1a1a', minHeight: '100vh', fontFamily: 'sans-serif' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.8rem', color: '#c5a059' }}>Tufte Gård Admin</h1>
                <div>
                    <span style={{ marginRight: '15px', color: '#888' }}>Inloggad.</span>
                    <button onClick={handleLogout} style={{ padding: '5px 15px', background: '#444', color: '#fff', border: 'none', borderRadius: '4px' }}>Logga ut</button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '5px', marginBottom: '20px', borderBottom: '1px solid #444' }}>
                <TabButton label="📅 Bokningsöversikt" active={activeTab === 'bookings'} onClick={() => setActiveTab('bookings')} />
                <TabButton label="✏️ Redigera Innehåll" active={activeTab === 'content'} onClick={() => setActiveTab('content')} />
                <TabButton label="🎉 Arrangemang" active={activeTab === 'events'} onClick={() => setActiveTab('events')} />
            </div>

            {message && <div style={{ backgroundColor: '#4caf50', padding: '10px', borderRadius: '4px', marginBottom: '20px', textAlign: 'center', fontWeight: 'bold' }}>{message}</div>}

            {activeTab === 'bookings' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px' }}>

                    {/* VÄNSTER: KALENDER-ÖVERSIKT */}
                    <div style={{ flex: '1', minWidth: '320px', background: '#222', padding: '20px', borderRadius: '8px', height: 'fit-content' }}>
                        <h3 style={{ color: '#c5a059', marginBottom: '15px' }}>Bokningskalender</h3>
                        <p style={{ fontSize: '0.9rem', color: '#ccc', marginBottom: '10px' }}>
                            Datum med <strong style={{ color: '#4caf50' }}>GRÖN</strong> prick har bokningar.<br />
                            Datum som är <strong style={{ color: '#ff4444' }}>RÖDA</strong> är fulla (≥ {MAX_BOOKINGS_PER_DAY} bokningar).
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <DatePicker
                                inline
                                locale="sv" // Svensk kalender (Mån-Sön)
                                selected={selectedDate ? new Date(selectedDate) : null}
                                onChange={handleOverviewDateSelect}
                                highlightDates={[
                                    { "react-datepicker__day--highlighted-custom-1": bookings.filter(b => bookingCounts[b.date] < MAX_BOOKINGS_PER_DAY).map(b => new Date(b.date)) },
                                    { "react-datepicker__day--highlighted-custom-2": Object.keys(bookingCounts).filter(d => bookingCounts[d] >= MAX_BOOKINGS_PER_DAY).map(d => new Date(d)) }
                                ]}
                            />
                        </div>
                        <div style={{ textAlign: 'center', marginTop: '15px' }}>
                            {selectedDate ? (
                                <button onClick={() => { setSelectedDate(null); setManualBooking(prev => ({ ...prev, date: '' })); }} style={{ background: '#444', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}>Visa alla bokningar</button>
                            ) : (
                                <span style={{ color: '#888', fontStyle: 'italic' }}>Klicka på ett datum för att filtrera listan & välja datum.</span>
                            )}
                        </div>
                    </div>

                    {/* HÖGER: LISTA & MANUELL */}
                    <div style={{ flex: '2', minWidth: '350px' }}>

                        {/* LISTA */}
                        <div style={{ marginBottom: '40px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #444', paddingBottom: '10px' }}>
                                <h3 style={{ margin: 0 }}>
                                    {selectedDate ? `Bokningar för ${selectedDate}` : (showHistory ? "Historiska Bokningar" : "Kommande Bokningar")}
                                </h3>
                                <div>
                                    <button
                                        onClick={() => setShowHistory(false)}
                                        style={{ background: !showHistory ? '#c5a059' : '#333', color: !showHistory ? '#000' : '#aaa', border: 'none', padding: '5px 10px', borderRadius: '4px 0 0 4px', cursor: 'pointer' }}
                                    >
                                        Kommande
                                    </button>
                                    <button
                                        onClick={() => setShowHistory(true)}
                                        style={{ background: showHistory ? '#c5a059' : '#333', color: showHistory ? '#000' : '#aaa', border: 'none', padding: '5px 10px', borderRadius: '0 4px 4px 0', cursor: 'pointer' }}
                                    >
                                        Historik
                                    </button>
                                </div>
                            </div>

                            {displayedBookings.length === 0 ? (
                                <p style={{ color: '#888' }}>Inga bokningar att visa.</p>
                            ) : (
                                Array.from(new Set(displayedBookings.map(b => b.date))).map(date => {
                                    const daysBookings = displayedBookings.filter(b => b.date === date);
                                    const dailyDishes = {};
                                    daysBookings.forEach(dbk => {
                                        if (dbk.dishes) Object.entries(dbk.dishes).forEach(([dish, count]) => {
                                            dailyDishes[dish] = (dailyDishes[dish] || 0) + parseInt(count || 0);
                                        });
                                    });
                                    const isFull = (bookingCounts[date] || 0) >= MAX_BOOKINGS_PER_DAY;

                                    return (
                                        <div key={date} style={{ marginBottom: '30px', background: '#2a2a2a', padding: '15px', borderRadius: '6px', borderLeft: isFull ? '5px solid #ff4444' : '5px solid #4caf50' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                <h4 style={{ fontSize: '1.2rem' }}>
                                                    {date} <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#aaa' }}>({daysBookings.length} bokningar {isFull ? '- FULLT' : ''})</span>
                                                </h4>
                                                <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#ccc' }}>
                                                    {Object.entries(dailyDishes).map(([d, c]) => <span key={d} style={{ marginLeft: '10px' }}>{c}x {d}</span>)}
                                                </div>
                                            </div>

                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                                <thead>
                                                    <tr style={{ background: '#333', color: '#aaa' }}>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Tid</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Namn</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Gäster</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Mat</th>
                                                        <th style={{ padding: '8px' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {daysBookings.map(b => (
                                                        <tr key={b.id} style={{ borderBottom: '1px solid #333' }}>
                                                            <td style={{ padding: '8px' }}>{b.time}</td>
                                                            <td style={{ padding: '8px' }}>
                                                                <strong>{b.name}</strong><br />
                                                                <span style={{ fontSize: '0.8rem', color: '#888' }}>{b.phone}</span>
                                                            </td>
                                                            <td style={{ padding: '8px' }}>{b.guests} st</td>
                                                            <td style={{ padding: '8px' }}>
                                                                {b.dishes ? Object.entries(b.dishes).map(([d, c]) => <div key={d}>{c}x {d}</div>) : '-'}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                                                <button onClick={() => setEditingBooking(b)} style={{ marginRight: '10px', color: '#c5a059', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✎</button>
                                                                <button onClick={() => deleteBooking(b.id)} style={{ color: '#ff4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })
                            )}

                            {showHistory && !selectedDate && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    {historyLoading ? (
                                        <span style={{ color: '#888' }}>Laddar...</span>
                                    ) : (
                                        <button onClick={fetchHistory} style={{ background: '#444', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}>
                                            Ladda äldre bokningar
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* MODAL FÖR REDIGERING AV BOKNING */}
                            {editingBooking && (
                                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
                                    <div style={{ background: '#333', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '500px' }}>
                                        <h3 style={{ color: '#c5a059', marginBottom: '20px' }}>Redigera Bokning</h3>
                                        <form onSubmit={handleUpdateBooking}>
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Datum</label>
                                                <input type="date" value={editingBooking.date} onChange={e => setEditingBooking({ ...editingBooking, date: e.target.value })} style={{ width: '100%', padding: '10px' }} />
                                            </div>
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Tid</label>
                                                <input type="text" value={editingBooking.time} onChange={e => setEditingBooking({ ...editingBooking, time: e.target.value })} style={{ width: '100%', padding: '10px' }} />
                                            </div>
                                            <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Namn</label>
                                                    <input type="text" value={editingBooking.name} onChange={e => setEditingBooking({ ...editingBooking, name: e.target.value })} style={{ width: '100%', padding: '10px' }} />
                                                </div>
                                                <div style={{ flex: 0.5 }}>
                                                    <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Gäster</label>
                                                    <input type="number" value={editingBooking.guests} onChange={e => setEditingBooking({ ...editingBooking, guests: e.target.value })} style={{ width: '100%', padding: '10px' }} />
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Telefon</label>
                                                <input type="text" value={editingBooking.phone} onChange={e => setEditingBooking({ ...editingBooking, phone: e.target.value })} style={{ width: '100%', padding: '10px' }} />
                                            </div>
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem' }}>Noteringar</label>
                                                <textarea value={editingBooking.notes || ''} onChange={e => setEditingBooking({ ...editingBooking, notes: e.target.value })} style={{ width: '100%', padding: '10px', height: '80px' }} />
                                            </div>

                                            <div style={{ background: '#222', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>
                                                <label style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '5px', display: 'block' }}>Mat (ändra antal)</label>
                                                {content.menu?.mainCourses?.map(dish => (
                                                    <div key={dish.title} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                        <span style={{ fontSize: '0.9rem' }}>{dish.title}</span>
                                                        <input
                                                            type="number" min="0" placeholder="0"
                                                            value={editingBooking.dishes?.[dish.title] || 0}
                                                            style={{ width: '50px', padding: '3px', textAlign: 'center' }}
                                                            onChange={(e) => setEditingBooking({
                                                                ...editingBooking,
                                                                dishes: {
                                                                    ...editingBooking.dishes,
                                                                    [dish.title]: parseInt(e.target.value)
                                                                }
                                                            })}
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button type="button" onClick={() => setEditingBooking(null)} style={{ flex: 1, padding: '10px', background: '#555', color: '#fff', border: 'none', cursor: 'pointer' }}>Avbryt</button>
                                                <button type="submit" style={{ flex: 1, padding: '10px', background: '#c5a059', color: '#000', border: 'none', cursor: 'pointer' }}>Spara Ändringar</button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ background: '#333', padding: '20px', borderRadius: '8px', marginTop: '40px' }}>
                            <h4 style={{ marginBottom: '15px', color: '#c5a059' }}>➕ Lägg till Manuell Bokning</h4>
                            <form onSubmit={addManualBooking}>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block' }}>Datum</label>
                                        <input
                                            type="date"
                                            required
                                            value={manualBooking.date}
                                            onChange={e => setManualBooking({ ...manualBooking, date: e.target.value, time: '' })}
                                            style={{ width: '100%', padding: '8px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block' }}>Tid</label>
                                        {/* Dropdown istället för input */}
                                        <select
                                            required
                                            value={manualBooking.time}
                                            onChange={e => setManualBooking({ ...manualBooking, time: e.target.value })}
                                            style={{ width: '100%', padding: '8px' }}
                                            disabled={!manualBooking.date}
                                        >
                                            <option value="" disabled>Välj tid...</option>
                                            {manualTimes.map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{ flex: 2 }}>
                                        <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block' }}>Namn</label>
                                        <input type="text" required value={manualBooking.name} onChange={e => setManualBooking({ ...manualBooking, name: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block' }}>Telefon</label>
                                        <input type="text" placeholder="Tlf" value={manualBooking.phone} onChange={e => setManualBooking({ ...manualBooking, phone: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                                    </div>
                                    <div style={{ flex: 0.5 }}>
                                        <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block' }}>Gäster</label>
                                        <select required value={manualBooking.guests} onChange={e => setManualBooking({ ...manualBooking, guests: e.target.value })} style={{ width: '100%', padding: '8px' }}>
                                            {Array.from({ length: 17 }, (_, i) => i + 4).map(num => (
                                                <option key={num} value={num}>{num}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Matval för manuell bokning */}
                                <div style={{ background: '#222', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>
                                    <label style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '5px', display: 'block' }}>Matval</label>
                                    {formData.menu?.mainCourses?.map(dish => (
                                        <div key={dish.title} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                            <span style={{ fontSize: '0.9rem' }}>{dish.title}</span>
                                            <input
                                                type="number" min="0" placeholder="0"
                                                style={{ width: '50px', padding: '3px', textAlign: 'center' }}
                                                onChange={(e) => handleManualDishChange(dish.title, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <button type="submit" style={{ width: '100%', padding: '10px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Spara Bokning</button>
                            </form>
                        </div>

                    </div>
                </div>
            )}

            {activeTab === 'content' && (
                <div>
                    {/* ... Content editing form (same as before) ... */}
                    <div style={{ marginBottom: '30px', background: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
                        <h2 style={{ color: '#c5a059', borderBottom: '1px solid #444', paddingBottom: '10px', marginBottom: '20px' }}>Öppettider & Kalender</h2>
                        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <h4>📅 Välj Öppna Dagar</h4>
                                <p style={{ fontSize: '0.85rem', color: '#bbb', marginBottom: '10px' }}>Klicka i datum här för att göra dem bokningsbara.</p>
                                <div style={{ marginBottom: '10px' }}><button onClick={generateWeekends} style={{ padding: '8px', fontSize: '0.8rem', cursor: 'pointer' }}>Autofyll Helger</button></div>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <DatePicker inline locale="sv" selected={null} onChange={handleCalendarDateChange} highlightDates={[{ "react-datepicker__day--highlighted-custom-1": parseDates(formData.calendar?.openDates) }]} dayClassName={(date) => (formData.calendar?.openDates || []).includes(new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0]) ? "calendar-open-day" : undefined} />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <h4>🕒 Tider</h4>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ color: '#c5a059' }}>Standard (Fre-Lör)</label>
                                    {formData.booking?.standardTimes?.map((t, i) => <div key={i} style={{ marginBottom: '5px', display: 'flex' }}><input value={t} onChange={e => handleArrayChange('booking', 'standardTimes', i, e.target.value)} style={{ flex: 1, padding: '5px' }} /><button onClick={() => handleDeleteItem('booking', 'standardTimes', i)} style={{ marginLeft: '5px', color: 'red' }}>X</button></div>)}
                                    <button onClick={() => handleAddItem('booking', 'standardTimes', '18:00')} style={{ fontSize: '0.8rem' }}>+ Tid</button>
                                </div>
                                <div>
                                    <label style={{ color: '#c5a059' }}>Söndag</label>
                                    {formData.booking?.sundayTimes?.map((t, i) => <div key={i} style={{ marginBottom: '5px', display: 'flex' }}><input value={t} onChange={e => handleArrayChange('booking', 'sundayTimes', i, e.target.value)} style={{ flex: 1, padding: '5px' }} /><button onClick={() => handleDeleteItem('booking', 'sundayTimes', i)} style={{ marginLeft: '5px', color: 'red' }}>X</button></div>)}
                                    <button onClick={() => handleAddItem('booking', 'sundayTimes', '15:00')} style={{ fontSize: '0.8rem' }}>+ Tid</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="admin-section">
                        <h2>Hemsidans Text</h2>

                        <div className="group-box">
                            <h3>Hero / Topp</h3>
                            <Input label="Rubrik" value={formData.hero?.title} onChange={(e) => handleChange('hero', 'title', e.target.value)} />
                            <Input label="Underrubrik" value={formData.hero?.subtitle} onChange={(e) => handleChange('hero', 'subtitle', e.target.value)} />
                            <TextArea label="Text" value={formData.hero?.description} onChange={(e) => handleChange('hero', 'description', e.target.value)} />
                            <Input label="Knapp" value={formData.hero?.buttonText} onChange={(e) => handleChange('hero', 'buttonText', e.target.value)} />

                            <h4 style={{ marginTop: '20px', color: '#c5a059' }}>🕒 Åpningstider (visas högst upp på sidan)</h4>
                            <Input label="Rubrik (Månedens navn oppdateres automatisk, f.eks. 'Åpningstider i juni')" value={formData.hero?.openingHoursTitle} onChange={(e) => handleChange('hero', 'openingHoursTitle', e.target.value)} />
                            <label style={{ display: 'block', fontSize: '0.8rem', color: '#aaa', marginBottom: '5px' }}>Rader (en per dag/tidsintervall)</label>
                            {formData.hero?.openingHours?.map((line, i) => (
                                <div key={i} style={{ marginBottom: '5px', display: 'flex' }}>
                                    <input
                                        value={line}
                                        onChange={e => handleArrayChange('hero', 'openingHours', i, e.target.value)}
                                        style={{ flex: 1, padding: '8px', background: '#333', color: '#fff', border: '1px solid #555' }}
                                    />
                                    <button onClick={() => handleDeleteItem('hero', 'openingHours', i)} style={{ marginLeft: '5px', color: 'red' }}>X</button>
                                </div>
                            ))}
                            <button onClick={() => handleAddItem('hero', 'openingHours', 'Måndag–Fredag: 12–19')} style={{ fontSize: '0.8rem' }}>+ Rad</button>
                            <div style={{ marginTop: '15px' }}>
                                <Input label="Notis under tiderna (t.ex. 'Siste dag i sommersesongen: 14. august')" value={formData.hero?.openingHoursNote} onChange={(e) => handleChange('hero', 'openingHoursNote', e.target.value)} />
                            </div>
                        </div>

                        <div className="group-box">
                            <h3>Intro / Filosofi</h3>
                            <Input label="Rubrik (Huvud)" value={formData.intro?.mainTitle} onChange={(e) => handleChange('intro', 'mainTitle', e.target.value)} />
                            <Input label="Etikett" value={formData.intro?.label} onChange={(e) => handleChange('intro', 'label', e.target.value)} />
                            <TextArea label="Text (Intro - Framtidsplaner)" value={formData.intro?.mainDescription} onChange={(e) => handleChange('intro', 'mainDescription', e.target.value)} />

                            <h4 style={{ marginTop: '15px', color: '#c5a059' }}>Del 1: Garden (Vänster bild)</h4>
                            <Input label="Rubrik (Garden)" value={formData.intro?.gardenTitle} onChange={(e) => handleChange('intro', 'gardenTitle', e.target.value)} />
                            <TextArea label="Text (Garden)" value={formData.intro?.gardenText} onChange={(e) => handleChange('intro', 'gardenText', e.target.value)} />

                            <h4 style={{ marginTop: '15px', color: '#c5a059' }}>Del 2: Maten (Höger bild)</h4>
                            <Input label="Rubrik (Maten)" value={formData.intro?.foodTitle} onChange={(e) => handleChange('intro', 'foodTitle', e.target.value)} />
                            <TextArea label="Text (Maten)" value={formData.intro?.foodText} onChange={(e) => handleChange('intro', 'foodText', e.target.value)} />
                        </div>

                        <div className="group-box">
                            <h3>Vinter / Säsong</h3>
                            <Input label="Rubrik" value={formData.winter?.title} onChange={(e) => handleChange('winter', 'title', e.target.value)} />
                            <TextArea label="Del 1" value={formData.winter?.textPart1} onChange={(e) => handleChange('winter', 'textPart1', e.target.value)} />
                            <TextArea label="Del 2" value={formData.winter?.textPart2} onChange={(e) => handleChange('winter', 'textPart2', e.target.value)} />
                        </div>
                        <div className="group-box">
                            <h3>Meny Texter</h3>
                            <Input label="Rubrik" value={formData.menu?.title} onChange={(e) => handleChange('menu', 'title', e.target.value)} />
                            <TextArea label="Intro" value={formData.menu?.intro} onChange={(e) => handleChange('menu', 'intro', e.target.value)} />
                            {/* ... Maträtter ... */}
                            <h4 style={{ marginTop: '20px' }}>Maträtter & Dryck</h4>
                            {formData.menu?.mainCourses?.map((dish, i) => (
                                <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', padding: '10px', background: '#333', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input placeholder="Maträtt" value={dish.title} onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'title', e.target.value)} style={{ flex: 1, padding: '8px' }} />
                                        <input placeholder="Pris/Info (t.ex '(Pris pr. pers...)')" value={dish.price} onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'price', e.target.value)} style={{ flex: 1, padding: '8px' }} />
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input placeholder="Beskrivning" value={dish.description} onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'description', e.target.value)} style={{ flex: 2, padding: '8px' }} />
                                        <select value={dish.category || 'mat'} onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'category', e.target.value)} style={{ flex: 1, padding: '8px', background: '#444', border: '1px solid #555', color: '#fff', height: '35px' }}>
                                            <option value="mat">MAT</option>
                                            <option value="maevl">MÆVL</option>
                                        </select>
                                        <button onClick={() => handleDeleteItem('menu', 'mainCourses', i)} style={{ color: 'red', padding: '8px' }}>X</button>
                                    </div>
                                    {(() => {
                                        const bulletCount = (dish.description || '').split('•').length - 1;
                                        if (bulletCount >= 2) {
                                            const variantLabels = (dish.description || '').split('•').slice(1).map(s => '• ' + s.trim().substring(0, 40));
                                            const variantAllergens = dish.variantAllergens || Array.from({ length: bulletCount }, () => []);
                                            return (
                                                <div style={{ marginTop: '10px' }}>
                                                    <label style={{ fontSize: '0.8rem', color: '#c5a059', display: 'block', marginBottom: '8px' }}>Allergener per variant</label>
                                                    {Array.from({ length: bulletCount }, (_, vi) => (
                                                        <div key={vi} style={{ marginBottom: '10px', padding: '8px 10px', background: '#2a2a2a', borderLeft: '3px solid #c5a059', borderRadius: '0 4px 4px 0' }}>
                                                            <label style={{ fontSize: '0.75rem', color: '#c5a059', display: 'block', marginBottom: '6px' }}>{variantLabels[vi] || `Variant ${vi + 1}`}</label>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {ALLERGEN_LIST.map(({ code, emoji, label }) => {
                                                                    const checked = (variantAllergens[vi] || []).includes(code);
                                                                    return (
                                                                        <label key={code} title={label} style={{ display: 'flex', alignItems: 'center', background: checked ? '#c5a059' : '#444', color: checked ? '#000' : '#ccc', padding: '3px 9px', borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', userSelect: 'none' }}>
                                                                            <input type="checkbox" checked={checked} onChange={e => {
                                                                                const updated = Array.from({ length: bulletCount }, (_, k) => [...(variantAllergens[k] || [])]);
                                                                                updated[vi] = e.target.checked ? [...updated[vi], code] : updated[vi].filter(c => c !== code);
                                                                                handleObjectArrayChange('menu', 'mainCourses', i, 'variantAllergens', updated);
                                                                            }} style={{ display: 'none' }} />
                                                                            {emoji}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }
                                        return (
                                            <div style={{ marginTop: '8px' }}>
                                                <label style={{ fontSize: '0.8rem', color: '#aaa', display: 'block', marginBottom: '5px' }}>Allergener</label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                    {ALLERGEN_LIST.map(({ code, emoji, label }) => {
                                                        const checked = (dish.allergens || []).includes(code);
                                                        return (
                                                            <label key={code} title={label} style={{ display: 'flex', alignItems: 'center', background: checked ? '#c5a059' : '#444', color: checked ? '#000' : '#ccc', padding: '3px 9px', borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', userSelect: 'none' }}>
                                                                <input type="checkbox" checked={checked} onChange={e => {
                                                                    const current = dish.allergens || [];
                                                                    const updated = e.target.checked ? [...current, code] : current.filter(c => c !== code);
                                                                    handleObjectArrayChange('menu', 'mainCourses', i, 'allergens', updated);
                                                                }} style={{ display: 'none' }} />
                                                                {emoji}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div>
                                            <label style={{ fontSize: '0.8rem', color: '#aaa', marginRight: '5px' }}>Datumstyrd:</label>
                                            <input type="date" value={dish.specificDate || ''} onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'specificDate', e.target.value)} style={{ padding: '5px', background: '#444', border: '1px solid #555', color: '#fff' }} />
                                        </div>
                                        {dish.specificDate && (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={dish.isExclusive || false}
                                                    onChange={e => handleObjectArrayChange('menu', 'mainCourses', i, 'isExclusive', e.target.checked)}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                <label style={{ fontSize: '0.8rem', color: '#c5a059' }}>Visa ENDAST denna rätt dette datum?</label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => handleAddItem('menu', 'mainCourses', { title: 'Ny', price: '', description: '', specificDate: '', isExclusive: false, category: 'mat' })}>+ Rätt</button>
                        </div>

                        <div className="group-box">
                            <h3>Bokningstext (Kontakt / Formulär)</h3>
                            <Input label="Rubrik" value={formData.booking?.title} onChange={(e) => handleChange('booking', 'title', e.target.value)} />
                            <TextArea label="Underrubrik / Intro" value={formData.booking?.subtitle} onChange={(e) => handleChange('booking', 'subtitle', e.target.value)} />
                            <TextArea label="Kontaktinfo (Botten)" value={formData.booking?.contactInfo} onChange={(e) => handleChange('booking', 'contactInfo', e.target.value)} />
                        </div>

                        <div className="group-box">
                            <h3>Bekräftelse Popup (Efter bokning)</h3>
                            <Input label="Rubrik" value={formData.confirmationPopup?.title} onChange={(e) => handleChange('confirmationPopup', 'title', e.target.value)} />
                            <TextArea label="Meddelande Rad 1" value={formData.confirmationPopup?.message1} onChange={(e) => handleChange('confirmationPopup', 'message1', e.target.value)} />
                            <TextArea label="Meddelande Rad 2" value={formData.confirmationPopup?.message2} onChange={(e) => handleChange('confirmationPopup', 'message2', e.target.value)} />
                        </div>

                        <div className="group-box">
                            <h3>Galleri / Bilder</h3>
                            <Input label="Rubrik" value={formData.gallery?.title} onChange={(e) => handleChange('gallery', 'title', e.target.value)} />

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px', marginTop: '15px' }}>
                                {formData.gallery?.images?.map((img, i) => (
                                    <div key={i} style={{ background: '#222', padding: '10px', borderRadius: '4px', border: '1px solid #444', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ height: '100px', overflow: 'hidden', marginBottom: '10px', borderRadius: '2px', position: 'relative' }}>
                                            <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '2px', background: 'rgba(0,0,0,0.5)' }}>
                                                <button onClick={() => handleMoveItem('gallery', 'images', i, -1)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px' }}>⬆️</button>
                                                <button onClick={() => handleMoveItem('gallery', 'images', i, 1)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px' }}>⬇️</button>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '5px' }}>
                                            <label style={{ fontSize: '0.7rem', color: '#aaa' }}>Kategori</label>
                                            <select
                                                value={img.category || 'vinter'}
                                                onChange={(e) => handleObjectArrayChange('gallery', 'images', i, 'category', e.target.value)}
                                                style={{ width: '100%', padding: '5px', background: '#333', color: '#fff', border: '1px solid #555', fontSize: '0.8rem' }}
                                            >
                                                <option value="vinter">Vinter</option>
                                                <option value="sommar">Sommer</option>
                                            </select>
                                        </div>
                                        <div style={{ marginBottom: '5px' }}>
                                            <label style={{ fontSize: '0.7rem', color: '#aaa' }}>URL</label>
                                            <input
                                                value={img.url}
                                                onChange={(e) => handleObjectArrayChange('gallery', 'images', i, 'url', e.target.value)}
                                                style={{ width: '100%', padding: '5px', background: '#333', color: '#fff', border: '1px solid #555', fontSize: '0.8rem' }}
                                            />
                                        </div>
                                        <button onClick={() => handleDeleteItem('gallery', 'images', i)} style={{ width: '100%', padding: '5px', background: '#d32f2f', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem', marginTop: 'auto' }}>Ta bort</button>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: '20px', padding: '15px', background: '#2a2a2a', borderRadius: '4px' }}>
                                <label style={{ display: 'block', marginBottom: '10px', color: '#c5a059', fontWeight: 'bold' }}>Ladda upp ny bild</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, 'gallery', 'images')}
                                    style={{ display: 'block', width: '100%', marginBottom: '10px', color: '#fff' }}
                                />
                                <p style={{ fontSize: '0.8rem', color: '#888' }}>
                                    (Max 2MB per bild. Bilden sparas direkt i hemsidans databas.)
                                </p>
                            </div>

                            <div style={{ marginTop: '10px', textAlign: 'center', color: '#888' }}>ELLER</div>

                            <button
                                onClick={() => handleAddItem('gallery', 'images', { url: '', category: 'vinter' })}
                                style={{ marginTop: '10px', padding: '10px', background: '#444', color: '#ccc', border: 'none', cursor: 'pointer', width: '100%' }}
                            >
                                + Lägg till tom bild-ruta (för URL)
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleSaveContent}
                        disabled={isSaving}
                        style={{ marginTop: '30px', padding: '15px', width: '100%', background: '#c5a059', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
                    >
                        {isSaving ? "Sparar..." : "SPARA ALLT"}
                    </button>
                </div>
            )}

            {activeTab === 'events' && (
                <div style={{ maxWidth: '800px', margin: '0 auto', background: '#222', padding: '20px', borderRadius: '4px' }}>
                    <h2 style={{ color: '#c5a059', borderBottom: '1px solid #444', paddingBottom: '10px', marginBottom: '20px' }}>Hantera Arrangemang</h2>

                    {formData.events?.map((event, i) => (
                        <div key={i} style={{ background: '#333', padding: '15px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #444' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                <Input label="Titel" value={event.title} onChange={(e) => handleObjectArrayChange('events', null, i, 'title', e.target.value)} />
                                <Input label="Datum (t.ex. '15 Juni' eller '15. Juni')" value={event.date} onChange={(e) => handleObjectArrayChange('events', null, i, 'date', e.target.value)} />
                            </div>
                            <TextArea label="Beskrivning" value={event.description} onChange={(e) => handleObjectArrayChange('events', null, i, 'description', e.target.value)} />
                            <Input label="Framhävd rad (svart text längst ner, t.ex. 'Inngang: 150 kr' — lämna tom för ingen)" value={event.highlight || ''} onChange={(e) => handleObjectArrayChange('events', null, i, 'highlight', e.target.value)} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                                <Input label="Bokningsdatum (YYYY-MM-DD)" value={event.bookingDate} onChange={(e) => handleObjectArrayChange('events', null, i, 'bookingDate', e.target.value)} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', background: '#222', padding: '5px 10px', borderRadius: '4px', border: '1px solid #444' }}>
                                        <input
                                            type="checkbox"
                                            checked={event.hidden || false}
                                            onChange={(e) => handleObjectArrayChange('events', null, i, 'hidden', e.target.checked)}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                        />
                                        <label style={{ color: '#ccc', fontSize: '0.9rem', cursor: 'pointer' }}>Dölj detta event</label>
                                    </div>
                                    <button onClick={() => handleDeleteItem('events', null, i)} style={{ padding: '8px 15px', background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer' }}>Ta bort Event</button>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={() => handleAddItem('events', null, { title: 'Nytt Event', date: '', description: '', highlight: '', bookingDate: '', imageUrl: '' })}
                        style={{ padding: '10px 20px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer', marginBottom: '30px' }}
                    >
                        + Lägg till Nytt Event
                    </button>

                    <button
                        onClick={handleSaveContent}
                        disabled={isSaving}
                        style={{ padding: '15px', width: '100%', background: '#c5a059', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
                    >
                        {isSaving ? "Sparar..." : "SPARA ALLT"}
                    </button>
                </div>
            )}
        </div>
    );
};

// ... Input components ...
const Input = ({ label, value, onChange }) => (
    <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '0.8rem', color: '#aaa', marginBottom: '3px' }}>{label}</label>
        <input type="text" value={value || ''} onChange={onChange} style={{ width: '100%', padding: '8px', background: '#333', color: '#fff', border: '1px solid #555' }} />
    </div>
);
const TextArea = ({ label, value, onChange }) => (
    <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '0.8rem', color: '#aaa', marginBottom: '3px' }}>{label}</label>
        <textarea value={value || ''} onChange={onChange} rows={3} style={{ width: '100%', padding: '8px', background: '#333', color: '#fff', border: '1px solid #555', fontFamily: 'sans-serif' }} />
    </div>
);
const TabButton = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{ padding: '10px 20px', border: 'none', background: active ? '#c5a059' : 'transparent', color: active ? '#000' : '#aaa', cursor: 'pointer', borderBottom: active ? '2px solid #fff' : 'none', fontSize: '1rem', fontWeight: active ? 'bold' : 'normal' }}>
        {label}
    </button>
);

export default Admin;
